import React from 'react';
import { MapPin, Phone, ShieldCheck, Crosshair, Map, Store, Users, Target, ArrowRight, CheckCircle2, Calendar, Info, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function Home() {
  useDocumentMeta('Kjergaard Sports | Firearms, Hunting Gear & Sporting Goods in Lake Benton, Minnesota', 'Visit Kjergaard Sports in Lake Benton, Minnesota for a large in-store selection of firearms, ammunition, optics, reloading supplies, safes, hunting blinds, accessories, classes, events, and shooting sports support.');
  const features = [{
    icon: <Store className="w-8 h-8 text-brand-hunter" data-id="element-279" />,
    title: 'A Huge In-Store Selection',
    description: 'Browse thousands of firearms and a massive inventory of gear in person. We stock the volume of a big-box store with the atmosphere of a local outfitter.'
  }, {
    icon: <Users className="w-8 h-8 text-brand-hunter" data-id="element-280" />,
    title: 'Practical Product Knowledge',
    description: 'Get guidance from staff who actually use the equipment. We help you find the right tool for the field, the range, or personal defense.'
  }, {
    icon: <Target className="w-8 h-8 text-brand-hunter" data-id="element-281" />,
    title: 'Built for Hunters, Shooters & Collectors',
    description: 'From upland bird hunting to precision reloading, our inventory is curated for serious outdoorsmen and shooting sports enthusiasts.'
  }];
  const categories = [{
    title: 'Firearms',
    image: 'https://cdn.magicpatterns.com/uploads/h2HHsk76CJxRY2pmsyL8mj/firearms-wall.jpg',
    alt: 'Firearms selection at Kjergaard Sports',
    description: 'Approximately 5,000 new and used firearms on hand, including options for hunting, sport shooting, personal defense, collecting, trap, skeet, waterfowl, upland, varmints, and more.',
    link: '/firearms'
  }, {
    title: 'Ammunition',
    image: 'https://cdn.magicpatterns.com/uploads/w1nfdCex88kVGKJTVUQepJ/ammunition-wall.jpg',
    alt: 'Ammunition and reloading supplies at Kjergaard Sports',
    description: 'A large in-store ammunition selection for hunting, target shooting, trap, skeet, waterfowl, upland, turkey, big game, personal defense, and popular calibers.',
    link: '/ammunition'
  }, {
    title: 'Optics',
    image: 'https://cdn.magicpatterns.com/uploads/1zvBEqUZKRM4BZCWhxgmNi/optics-cases.jpg',
    alt: 'Scopes and optics available at Kjergaard Sports',
    description: 'Scopes, binoculars, spotting scopes, rangefinders, and optics options for hunters, target shooters, and outdoor use.',
    link: '/optics'
  }, {
    title: 'Reloading Supplies',
    image: 'https://cdn.magicpatterns.com/uploads/8918DCMwwSgbp8VrUA2ZyG/reloading-supplies.jpg',
    alt: 'Reloading supplies at Kjergaard Sports',
    description: 'Bullets, powders, primers, presses, dies, scales, tools, and reloading support for customers who enjoy building safe, accurate loads.',
    link: '/reloading-supplies'
  }, {
    title: 'Safes & Accessories',
    image: 'https://cdn.magicpatterns.com/uploads/esrRsJE1XbS5zcQi7PgR9E/safes-display.jpg',
    alt: 'Gun safes and secure storage at Kjergaard Sports',
    description: 'Safes, cleaning supplies, cases, holsters, magazines, rests, targets, clay birds, firearm maintenance items, and shooting accessories.',
    link: '/safes-accessories'
  }, {
    title: 'Hunting Gear & Blinds',
    image: 'https://cdn.magicpatterns.com/uploads/916kAQgnnbRYFKKSKvhkwD/hunting-apparel-gear.jpg',
    alt: 'Large in-store selection at Kjergaard Sports in Lake Benton, Minnesota',
    description: 'Hunting blinds and outdoor gear for customers preparing for the field.',
    link: '/hunting-gear-blinds'
  }];
  const services = ['Firearm selection guidance', 'Scope and accessory installation questions', 'Gunsmithing / in-store support', 'Permit to Carry class information', 'NFA / Form 4 in-store support if currently available', 'Event and demo shoot updates'];
  const events = [{
    title: 'Minnesota Permit to Carry Classes',
    description: 'In-person instruction covering safety, legal requirements, and practical fundamentals.'
  }, {
    title: 'Buffalo Ridge Demo Shoot',
    description: 'Annual opportunity to test and experience a variety of firearms in a safe, supervised environment.'
  }, {
    title: 'Store Events & Announcements',
    description: 'Special promotions, manufacturer days, and seasonal store updates.'
  }];
  const reviews = [{
    text: 'Very friendly and the staff has a lot of knowledge. Prices are very reasonable.',
    author: 'Marty Hilfers'
  }, {
    text: 'Huge inventory with a nice store. A little out of the way, but well worth the trip.',
    author: 'Jacob Raml'
  }, {
    text: 'Fantastic experience today. Extremely friendly and helpful. Great prices too! Worth the gas to get there!',
    author: 'J D'
  }];
  return <div className="-mt-20 lg:-mt-24" data-id="element-282">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex flex-col justify-center pt-20" data-id="element-283">
        <div className="absolute inset-0 z-0" data-id="element-284">
          <img src="https://cdn.magicpatterns.com/uploads/4PEc13YMi3QHpCzosrhrkQ/store-interior-firearms-wide.jpg" alt="Inside Kjergaard Sports — a large rural sporting goods and firearms store in Lake Benton, Minnesota" className="w-full h-full object-cover object-center" data-id="element-285" />
          <div className="absolute inset-0 bg-brand-hunter/65 mix-blend-multiply" data-id="element-286"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/80 via-transparent to-transparent" data-id="element-287"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 lg:py-24" data-id="element-288">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-3xl" data-id="element-289">
            <div className="flex items-center space-x-2 mb-6" data-id="element-290">
              <div className="h-px w-8 bg-brand-leather" data-id="element-291"></div>
              <span className="text-brand-bone uppercase tracking-widest text-sm font-semibold" data-id="element-292">
                Lake Benton, Minnesota
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-serif text-brand-offwhite mb-6 leading-tight" data-id="element-293">
              Kjergaard Sports
            </h1>

            <p className="text-lg md:text-xl text-brand-cream mb-4 leading-relaxed max-w-2xl" data-id="element-294">
              A rural shooting sports and hunting supply destination with
              approximately 5,000 guns on hand, plus ammunition, optics,
              reloading supplies, safes, hunting blinds, accessories, and
              in-store expertise.
            </p>

            <p className="text-xl md:text-2xl font-serif italic text-brand-leather mb-10" data-id="element-295">
              Where the service and price are worth the trip.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-16" data-id="element-296">
              <Link to="/contact" className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-8 py-4 rounded-sm font-medium text-center transition-colors text-lg" data-id="element-297">
                Plan Your Visit
              </Link>
              <a href="tel:507-368-4696" className="border-2 border-brand-bone hover:border-brand-leather hover:bg-brand-leather/10 text-brand-offwhite px-8 py-4 rounded-sm font-medium text-center transition-colors text-lg flex items-center justify-center" data-id="element-298">
                <Phone className="w-5 h-5 mr-2" data-id="element-299" />
                Call 507-368-4696
              </a>
              <Link to="/contact" className="bg-brand-offwhite/10 hover:bg-brand-offwhite/20 text-brand-offwhite px-8 py-4 rounded-sm font-medium text-center transition-colors text-lg flex items-center justify-center backdrop-blur-sm" data-id="element-300">
                <MapPin className="w-5 h-5 mr-2" data-id="element-301" />
                Get Directions
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-brand-bone/30" data-id="element-302">
              <div className="flex items-center text-brand-bone" data-id="element-303">
                <ShieldCheck className="w-5 h-5 mr-3 text-brand-leather flex-shrink-0" data-id="element-304" />
                <span className="text-sm font-medium" data-id="element-305">5,000+ guns on hand</span>
              </div>
              <div className="flex items-center text-brand-bone" data-id="element-306">
                <Map className="w-5 h-5 mr-3 text-brand-leather flex-shrink-0" data-id="element-307" />
                <span className="text-sm font-medium" data-id="element-308">
                  In-store service only
                </span>
              </div>
              <div className="flex items-center text-brand-bone" data-id="element-309">
                <Crosshair className="w-5 h-5 mr-3 text-brand-leather flex-shrink-0" data-id="element-310" />
                <span className="text-sm font-medium" data-id="element-311">
                  Hunting, shooting sports, CCW &amp; collectors
                </span>
              </div>
              <div className="flex items-center text-brand-bone" data-id="element-312">
                <MapPin className="w-5 h-5 mr-3 text-brand-leather flex-shrink-0" data-id="element-313" />
                <span className="text-sm font-medium" data-id="element-314">
                  Lake Benton, Minnesota
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-brand-offwhite bg-texture" data-id="element-315">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-316">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-317">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-318">
              Worth the Trip Since the First Visit
            </h2>
            <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-319">
              Kjergaard Sports is not a small generic shop. It is an enormous
              rural destination store with a deep in-store selection,
              knowledgeable service, and a practical hunting and shooting sports
              focus. Many customers travel long distances because they can
              browse a large selection in person and get guidance from people
              who understand the products.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-320">
            {features.map((feature, index) => <div key={index} className="bg-brand-cream p-8 rounded-sm border border-brand-bone shadow-sm hover:shadow-md transition-shadow" data-id="element-321">
                <div className="w-16 h-16 bg-brand-bone rounded-full flex items-center justify-center mb-6" data-id="element-322">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-serif text-brand-charcoal mb-4" data-id="element-323">
                  {feature.title}
                </h3>
                <p className="text-brand-charcoal/75 leading-relaxed" data-id="element-324">
                  {feature.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="selection" className="py-20 bg-brand-bone" data-id="element-325">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-326">
          <div className="mb-12" data-id="element-327">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-328">
              What You'll Find In Store
            </h2>
            <div className="h-1 w-20 bg-brand-leather" data-id="element-329"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-330">
            {categories.map((category, index) => <div key={index} className="bg-brand-offwhite rounded-sm overflow-hidden group border border-brand-cream shadow-sm hover:shadow-md transition-all" data-id="element-331">
                <div className="relative h-64 overflow-hidden" data-id="element-332">
                  <img src={category.image} alt={category.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-333" />
                  <div className="absolute inset-0 bg-brand-charcoal/10 group-hover:bg-transparent transition-colors" data-id="element-334"></div>
                </div>
                <div className="p-6" data-id="element-335">
                  <h3 className="text-2xl font-serif text-brand-charcoal mb-3" data-id="element-336">
                    {category.title}
                  </h3>
                  <p className="text-brand-charcoal/75 mb-6 line-clamp-3" data-id="element-337">
                    {category.description}
                  </p>
                  <Link to={category.link} className="inline-flex items-center text-brand-hunter font-medium hover:text-brand-wood transition-colors" data-id="element-338">
                    Learn More <ArrowRight className="w-4 h-4 ml-2" data-id="element-339" />
                  </Link>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* STORE TOUR GALLERY */}
      <section className="py-20 bg-brand-offwhite" data-id="element-340">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-341">
          <div className="mb-12" data-id="element-342">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-343">
              A Store Worth the Drive
            </h2>
            <div className="h-1 w-20 bg-brand-leather mb-6" data-id="element-344"></div>
            <p className="text-lg text-brand-charcoal/80 max-w-3xl leading-relaxed" data-id="element-345">
              Approximately 5,000 firearms on hand, racks of rifles and
              shotguns, walls of ammunition and reloading components, and a
              workshop built for real in-person help. Take a look around before
              you make the trip.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-346">
            <div className="overflow-hidden rounded-sm shadow-sm" data-id="element-347">
              <img src="https://cdn.magicpatterns.com/uploads/1BGu7nrTdE3WRCeMKF6WrW/store-tour-wide.jpg" alt="Wide view of Kjergaard Sports interior" className="w-full h-72 object-cover hover:scale-105 transition-transform duration-700" data-id="element-348" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm" data-id="element-349">
              <img src="https://cdn.magicpatterns.com/uploads/6KbpNKGWUu4crVHhGcrQiK/home-hero-store-interior.jpg" alt="Main floor at Kjergaard Sports" className="w-full h-72 object-cover hover:scale-105 transition-transform duration-700" data-id="element-350" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm" data-id="element-351">
              <img src="https://cdn.magicpatterns.com/uploads/kENmASH4b2qj4AE9LqDfT7/mounted-rural-detail.jpg" alt="Rural atmosphere and mounted animals at Kjergaard Sports" className="w-full h-72 object-cover hover:scale-105 transition-transform duration-700" data-id="element-352" />
            </div>
          </div>

          <div className="text-center" data-id="element-353">
            <Link to="/store-tour" className="inline-flex items-center text-brand-hunter font-medium hover:text-brand-wood transition-colors text-lg" data-id="element-354">
              Take the Full Store Tour <ArrowRight className="w-5 h-5 ml-2" data-id="element-355" />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 bg-brand-cream" data-id="element-356">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-357">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-358">
            <div className="order-2 lg:order-1" data-id="element-359">
              <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-360">
                A Store Built Around Real In-Person Help
              </h2>
              <p className="text-lg text-brand-charcoal/80 mb-8 leading-relaxed" data-id="element-361">
                Kjergaard Sports is a practical, knowledgeable, local store
                where customers can ask questions, compare products in person,
                and get help choosing the right equipment. We take pride in
                offering humble, rural service—not a corporate retail
                experience.
              </p>

              <ul className="space-y-4 mb-10" data-id="element-362">
                {services.map((service, index) => <li key={index} className="flex items-start" data-id="element-363">
                    <CheckCircle2 className="w-6 h-6 text-brand-leather mr-3 flex-shrink-0 mt-0.5" data-id="element-364" />
                    <span className="text-brand-charcoal font-medium" data-id="element-365">
                      {service}
                    </span>
                  </li>)}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-366">
                <Link to="/contact" className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-8 py-3 rounded-sm font-medium text-center transition-colors" data-id="element-367">
                  Ask a Question
                </Link>
                <a href="tel:507-368-4696" className="border border-brand-charcoal/20 hover:border-brand-leather hover:text-brand-leather text-brand-charcoal px-8 py-3 rounded-sm font-medium text-center transition-colors" data-id="element-368">
                  Call the Store
                </a>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative" data-id="element-369">
              <div className="absolute inset-0 bg-brand-sage translate-x-4 translate-y-4 rounded-sm" data-id="element-370"></div>
              <img src="https://cdn.magicpatterns.com/uploads/6uBKPbVFcv4swNygT9aBpc/services-gunsmithing-workshop.jpg" alt="Gunsmithing workshop and in-store support at Kjergaard Sports" className="relative z-10 w-full h-auto rounded-sm shadow-lg object-cover aspect-[4/3]" data-id="element-371" />
            </div>
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section id="events" className="py-20 bg-brand-hunter text-brand-offwhite relative overflow-hidden" data-id="element-372">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-373">
          <img src="https://cdn.magicpatterns.com/uploads/4W49omroAHfAdaTefJNmcK/targets-shooting-supplies.jpg" alt="Reactive steel targets and shooting supplies at Kjergaard Sports" className="w-full h-full object-cover" data-id="element-374" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-375">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-376">
            <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-377">
              Classes, Events &amp; Store Updates
            </h2>
            <div className="h-1 w-20 bg-brand-leather mx-auto" data-id="element-378"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-379">
            {events.map((event, index) => <div key={index} className="bg-brand-charcoal/40 backdrop-blur-sm border border-brand-sage/30 p-8 rounded-sm" data-id="element-380">
                <Calendar className="w-8 h-8 text-brand-leather mb-4" data-id="element-381" />
                <h3 className="text-xl font-serif mb-3" data-id="element-382">{event.title}</h3>
                <p className="text-brand-offwhite/80 mb-6" data-id="element-383">
                  {event.description}
                </p>
                <div className="flex flex-col space-y-3" data-id="element-384">
                  <a href="tel:507-368-4696" className="text-sm font-medium text-brand-leather hover:text-brand-bone transition-colors" data-id="element-385">
                    Call for Details →
                  </a>
                </div>
              </div>)}
          </div>

          <div className="bg-brand-charcoal/60 backdrop-blur-md border border-brand-sage/50 p-6 rounded-sm flex items-start max-w-4xl mx-auto" data-id="element-386">
            <Info className="w-6 h-6 text-brand-leather mr-4 flex-shrink-0 mt-1" data-id="element-387" />
            <p className="text-brand-offwhite/90 text-lg" data-id="element-388">
              Class dates and event details change. Call the store or follow
              Kjergaard Sports on Facebook for current updates.
            </p>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-24 bg-brand-cream bg-texture" data-id="element-389">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-390">
          <div className="text-center mb-16" data-id="element-391">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-392">
              Why Customers Say It's Worth the Drive
            </h2>
            <div className="h-1 w-20 bg-brand-leather mx-auto" data-id="element-393"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-10" data-id="element-394">
            {reviews.map((review, index) => <div key={index} className="flex flex-col" data-id="element-395">
                <Quote className="w-10 h-10 text-brand-bone mb-4" data-id="element-396" />
                <blockquote className="flex-grow" data-id="element-397">
                  <p className="text-xl font-serif text-brand-charcoal leading-relaxed mb-6 italic" data-id="element-398">
                    "{review.text}"
                  </p>
                </blockquote>
                <div className="pt-4 border-t border-brand-wood/20" data-id="element-399">
                  <p className="font-medium text-brand-hunter uppercase tracking-wider text-sm" data-id="element-400">
                    — {review.author}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* PLAN YOUR VISIT */}
      <section className="py-20 bg-brand-bone" data-id="element-401">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-402">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-403">
            <div className="grid grid-cols-2 gap-4" data-id="element-404">
              <div className="col-span-2" data-id="element-405">
                <img src="https://cdn.magicpatterns.com/uploads/f5Jp1yjaV9t3nwDxVsC5wU/exterior-front-building.jpg" alt="Kjergaard Sports storefront in Lake Benton, Minnesota" className="w-full h-64 object-cover rounded-sm shadow-sm" data-id="element-406" />
              </div>
              <div data-id="element-407">
                <img src="https://cdn.magicpatterns.com/uploads/r78gFzpmsNjcdavFnWNwrr/property-sign.jpg" alt="Kjergaard Sports property sign" className="w-full h-48 object-cover rounded-sm shadow-sm" data-id="element-408" />
              </div>
              <div data-id="element-409">
                <img src="https://cdn.magicpatterns.com/uploads/pvSYjNceL5zo92xh1cTiRL/road-sign.jpg" alt="Kjergaard Sports road sign" className="w-full h-48 object-cover rounded-sm shadow-sm" data-id="element-410" />
              </div>
            </div>

            <div data-id="element-411">
              <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-412">
                Plan Your Visit to Lake Benton
              </h2>
              <p className="text-lg text-brand-charcoal/80 mb-8 leading-relaxed" data-id="element-413">
                We are a rural destination store, and we know our customers
                travel to see us. When you make the trip, you'll find a massive
                selection and staff ready to help.
              </p>

              <div className="space-y-6 mb-10" data-id="element-414">
                <div className="flex items-start" data-id="element-415">
                  <MapPin className="w-6 h-6 text-brand-hunter mr-4 mt-1 flex-shrink-0" data-id="element-416" />
                  <div data-id="element-417">
                    <h3 className="font-medium text-brand-charcoal text-lg mb-1" data-id="element-418">
                      Address
                    </h3>
                    <p className="text-brand-charcoal/80" data-id="element-419">
                      1951 200th Avenue
                      <br data-id="element-420" />
                      Lake Benton, Minnesota 56149
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-421">
                  <Phone className="w-6 h-6 text-brand-hunter mr-4 mt-1 flex-shrink-0" data-id="element-422" />
                  <div data-id="element-423">
                    <h3 className="font-medium text-brand-charcoal text-lg mb-1" data-id="element-424">
                      Phone
                    </h3>
                    <p className="text-brand-charcoal/80" data-id="element-425">507-368-4696</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-426">
                <a href="https://maps.google.com/?q=1951+200th+Avenue+Lake+Benton+Minnesota+56149" target="_blank" rel="noopener noreferrer" className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-8 py-3 rounded-sm font-medium text-center transition-colors flex items-center justify-center" data-id="element-427">
                  <MapPin className="w-5 h-5 mr-2" data-id="element-428" />
                  Get Directions
                </a>
                <a href="tel:507-368-4696" className="border border-brand-charcoal/20 hover:border-brand-leather hover:text-brand-leather text-brand-charcoal px-8 py-3 rounded-sm font-medium text-center transition-colors flex items-center justify-center" data-id="element-429">
                  <Phone className="w-5 h-5 mr-2" data-id="element-430" />
                  Call 507-368-4696
                </a>
                <Link to="/contact" className="bg-brand-offwhite hover:bg-brand-cream text-brand-charcoal px-8 py-3 rounded-sm font-medium text-center transition-colors border border-brand-cream" data-id="element-431">
                  Ask a Question
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}